import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "dragHandle", "nameDisplay", "nameInput", "statusIcon"]

  // Pipeline strategy icon mapping (matches AssetStatusIndicatorComponent::PIPELINE_ICONS)
  static PIPELINE_ICONS = {
    full: { icon: "fa-shield-check", color: "text-green-600", title: "Full Protection" },
    visible_only: { icon: "fa-eye", color: "text-blue-600", title: "Visible Only" },
    invisible_only: { icon: "fa-fingerprint", color: "text-purple-600", title: "Invisible Only" },
    passthrough: { icon: "fa-forward", color: "text-gray-400", title: "Passthrough" }
  }
  static outlets = ["asset-manager"]
  static values = {
    assetId: String,
    index: Number,
    settingsUrl: String,
    updateUrl: String
  }

  #isSelected = false
  #originalName = ""

  connect() {
    this.element.setAttribute("tabindex", "-1")
  }

  select(event) {
    if (event.target.closest('[data-asset-item-target="checkbox"]')) return
    if (event.target.closest("button") || event.target.closest("a")) return

    this.#notifyManager("select", {
      shift: event.shiftKey,
      meta: event.metaKey || event.ctrlKey,
      index: this.indexValue
    })
  }

  toggle(event) {
    this.#notifyManager("select", {
      shift: event.shiftKey,
      meta: true,
      index: this.indexValue
    })
  }

  setSelected(selected) {
    this.#isSelected = selected

    if (this.hasCheckboxTarget) {
      this.checkboxTarget.checked = selected
    }

    if (selected) {
      this.element.classList.add("bg-blue-50")
    } else {
      this.element.classList.remove("bg-blue-50")
    }
  }

  focus() {
    this.element.focus()
    this.element.classList.add("ring-2", "ring-blue-500", "ring-inset")
  }

  blur() {
    this.element.classList.remove("ring-2", "ring-blue-500", "ring-inset")
  }

  get isSelected() {
    return this.#isSelected
  }

  openLightbox(event) {
    event.stopPropagation()

    const lightboxData = JSON.parse(event.currentTarget.dataset.lightbox || "{}")
    this.#notifyManager("openLightbox", { lightboxData, index: this.indexValue })
  }

  updatePipeline(event) {
    const select = event.target
    const previousValue = select.dataset.previousValue || select.value
    const strategy = select.value

    select.dataset.previousValue = strategy

    const settings = this.#pipelineToSettings(strategy)

    fetch(this.settingsUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.#getCsrfToken()
      },
      body: JSON.stringify(settings)
    }).then(response => {
      if (response.ok) {
        this.#updateStatusIcon(strategy)
      } else {
        console.error("Failed to update pipeline strategy")
        select.value = previousValue
        select.dataset.previousValue = previousValue
      }
    }).catch(error => {
      console.error("Failed to update pipeline strategy:", error)
      select.value = previousValue
      select.dataset.previousValue = previousValue
    })
  }

  #updateStatusIcon(pipeline) {
    if (!this.hasStatusIconTarget) return

    const config = this.constructor.PIPELINE_ICONS[pipeline]
    if (!config) return

    const iconEl = this.statusIconTarget.querySelector("i")
    if (!iconEl) return

    // Remove all color classes and icon classes, then add new ones
    iconEl.className = `fadr ${config.icon} ${config.color}`
    this.statusIconTarget.title = config.title
    this.statusIconTarget.dataset.pipeline = pipeline
  }

  startRename(event) {
    event.stopPropagation()

    if (!this.hasNameDisplayTarget || !this.hasNameInputTarget) return

    this.#originalName = this.nameDisplayTarget.textContent.trim()
    this.nameInputTarget.value = this.#originalName
    this.nameDisplayTarget.classList.add("hidden")
    this.nameInputTarget.classList.remove("hidden")
    this.nameInputTarget.focus()
    this.nameInputTarget.select()
  }

  finishRename(event) {
    if (event.type === "keydown") {
      event.preventDefault()
    }

    const newName = this.nameInputTarget.value.trim()

    if (newName && newName !== this.#originalName) {
      this.#saveRename(newName)
    } else {
      this.#hideRenameInput()
    }
  }

  cancelRename(event) {
    event.preventDefault()
    this.nameInputTarget.value = this.#originalName
    this.#hideRenameInput()
  }

  #hideRenameInput() {
    this.nameInputTarget.classList.add("hidden")
    this.nameDisplayTarget.classList.remove("hidden")
  }

  #saveRename(newName) {
    fetch(this.updateUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.#getCsrfToken()
      },
      body: JSON.stringify({ asset: { name: newName } })
    }).then(response => {
      if (response.ok) {
        this.nameDisplayTarget.textContent = newName
        this.nameDisplayTarget.title = newName
        this.#originalName = newName
      } else {
        console.error("Failed to rename asset")
        this.nameInputTarget.value = this.#originalName
      }
      this.#hideRenameInput()
    }).catch(error => {
      console.error("Failed to rename asset:", error)
      this.nameInputTarget.value = this.#originalName
      this.#hideRenameInput()
    })
  }

  #pipelineToSettings(pipeline) {
    switch (pipeline) {
      case "full":
        return { visible_watermark_enabled: true, invisible_watermark_enabled: true }
      case "visible_only":
        return { visible_watermark_enabled: true, invisible_watermark_enabled: false }
      case "invisible_only":
        return { visible_watermark_enabled: false, invisible_watermark_enabled: true }
      case "passthrough":
      default:
        return { visible_watermark_enabled: false, invisible_watermark_enabled: false }
    }
  }

  #getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  #notifyManager(action, options = {}) {
    if (!this.hasAssetManagerOutlet) {
      console.warn("asset-item: No asset-manager outlet connected")
      return
    }

    switch (action) {
      case "select":
        this.assetManagerOutlet.select(this.assetIdValue, options)
        break
      case "openLightbox":
        this.assetManagerOutlet.openLightbox(options.lightboxData, options.index)
        break
    }
  }
}
